import React, { useEffect, useMemo, useState } from "react";

import { Search, User } from "lucide-react";

import { Card } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/utils";
import { Seller, fetchSellers } from "~/services/sellerService";

interface SellersListProps {
  onSellerSelect: (seller: Seller) => void;
  selectedSeller: Seller | null;
}

const SellersList: React.FC<SellersListProps> = ({
  onSellerSelect,
  selectedSeller,
}) => {
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const loadSellers = async () => {
      setIsLoading(true);
      try {
        const data = await fetchSellers();
        setSellers(data);
      } catch (error) {
        console.error("Error loading sellers:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSellers();
  }, []);

  const filteredSellers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return sellers;
    return sellers.filter((seller) =>
      `${seller.name} ${seller.lastname}`.toLowerCase().includes(query)
    );
  }, [sellers, search]);

  if (isLoading) {
    return (
      <Card className="p-4">
        <h2 className="mb-3 text-lg font-semibold">Vendedores</h2>
        <Skeleton className="mb-3 h-9 w-full sm:w-72" />
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="h-11 w-full rounded-md" />
          ))}
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-lg font-semibold">Vendedores</h2>
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar vendedor..."
            className="h-9 pl-8"
          />
        </div>
      </div>

      {filteredSellers.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          {search ? "No se encontraron vendedores" : "No hay vendedores"}
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {filteredSellers.map((seller) => {
            const isSelected = selectedSeller?.id === seller.id;
            return (
              <button
                type="button"
                key={seller.id}
                onClick={() => onSellerSelect(seller)}
                className={cn(
                  "flex items-center gap-2 rounded-md border p-2 text-left transition-colors",
                  isSelected
                    ? "border-primary bg-primary/10"
                    : "border-transparent bg-muted/40 hover:bg-muted"
                )}
              >
                <div
                  className={cn(
                    "shrink-0 rounded-full p-1",
                    isSelected
                      ? "bg-primary/20 text-primary"
                      : "bg-primary/10 text-primary"
                  )}
                >
                  <User size={16} />
                </div>
                <span className="min-w-0 truncate text-sm font-medium">
                  {seller.name} {seller.lastname}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </Card>
  );
};

export default SellersList;
